export function TextField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  error,
  required,
  disabled,
  hint,
  dir,
  ...props
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">
        {label}
        {required ? ' *' : ''}
      </span>
      <input
        id={id}
        className={error ? 'input input-error' : 'input'}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
        dir={dir}
        {...props}
      />
      {hint ? <span id={hintId} className="field-hint">{hint}</span> : null}
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </label>
  );
}

export function TextArea({ id, label, value, onChange, error, required, disabled, dir, rows = 4, hint }) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">
        {label}
        {required ? ' *' : ''}
      </span>
      <textarea
        id={id}
        className={error ? 'input input-error' : 'input'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
        dir={dir}
        rows={rows}
      />
      {hint ? <span id={hintId} className="field-hint">{hint}</span> : null}
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </label>
  );
}

export function SelectField({ id, label, value, onChange, options, error, required, disabled, hint, ...props }) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">
        {label}
        {required ? ' *' : ''}
      </span>
      <select
        id={id}
        className={error ? 'input input-error' : 'input'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        required={required}
        disabled={disabled}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={Boolean(option.disabled)}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <span id={hintId} className="field-hint">{hint}</span> : null}
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </label>
  );
}

export function SwitchField({ id, label, checked, onChange, disabled }) {
  return (
    <div className="switch-field">
      <label className="switch-field-control" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
        />
        <span>{label}</span>
      </label>
    </div>
  );
}

export function FileField({
  id,
  label,
  accept,
  file,
  onChange,
  error,
  hint,
  previewUrl,
  disabled,
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className={error ? 'input input-error file-input' : 'input file-input'}
        type="file"
        accept={accept}
        onChange={(event) => onChange(event.target.files?.[0] || null)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        disabled={disabled}
      />
      {file ? <span className="field-hint">{file.name}</span> : null}
      {previewUrl ? <img className="file-preview" src={previewUrl} alt="" /> : null}
      {hint ? <span id={hintId} className="field-hint">{hint}</span> : null}
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </div>
  );
}

export function BilingualField({
  id,
  labelAr,
  labelEn,
  valueAr,
  valueEn,
  onChangeAr,
  onChangeEn,
  errorAr,
  errorEn,
  multiline,
  required = true,
  disabled,
}) {
  const Input = multiline ? TextArea : TextField;
  return (
    <div className="bilingual-grid">
      <Input id={`${id}-ar`} label={labelAr} value={valueAr} onChange={onChangeAr} error={errorAr} dir="rtl" required={required} disabled={disabled} />
      <Input id={`${id}-en`} label={labelEn} value={valueEn} onChange={onChangeEn} error={errorEn} dir="ltr" required={required} disabled={disabled} />
    </div>
  );
}

export function ColorField({ id, label, value, onChange, error }) {
  const errorId = error ? `${id}-error` : undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">{label} *</span>
      <div className="color-field">
        <input
          id={`${id}-swatch`}
          type="color"
          value={/^#([0-9A-Fa-f]{6})$/.test(value) ? value : '#0F766E'}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          aria-label={label}
        />
        <input
          id={id}
          className={error ? 'input input-error' : 'input'}
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
        />
      </div>
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </label>
  );
}

export function WeightInput({ id, label, value, onChange, error, disabled }) {
  const errorId = error ? `${id}-error` : undefined;
  return (
    <label className="field" htmlFor={id}>
      {label ? <span className="field-label">{label}</span> : null}
      <input
        id={id}
        className={error ? 'input input-error weight-input' : 'input weight-input'}
        type="number"
        step="0.01"
        min="-100"
        max="100"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId}
        aria-label={label || id}
      />
      {error ? <span id={errorId} className="field-error">{error}</span> : null}
    </label>
  );
}
